import { useFormikContext } from "formik";
import React, { FC, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useLocale } from "../../../../contexts/localeContext";
import { TranslationKeys } from "../../../../contexts/TranslationKeys";
import { formatTime } from "../../../../services/utilities/dateFormatting";
import { getRecipientData } from "../../../../utilities/NotificationsUtils";
import ActivityFrame from "../../../activity-frame/activity-frame";
import { buildRecurrencePatternSummary, buildRecurrenceRangeSummary } from "./summary-helper";
import { Button } from "../../../../components/button/Button";
import { WizardSummaryLayout } from "../../../../components/wizard/wizard-summary-layout";

interface SummaryProps {
    onBack: (event) => void
    taskDescription: string;
}

const Summary: FC<SummaryProps> = ({ onBack, taskDescription }) => {
    const { translate } = useLocale();
    const { values } = useFormikContext<any>();
    const [sendToMasterData, setSendToMasterData] = useState<any>([]);
    const [copyToMasterData, setCopyToMasterData] = useState<any>([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        getData();
    }, []);

    const getData = async () => {
        setBusy(true);
        try {
            const { toRecipientsData, ccRecipientsData } = await getRecipientData(translate, values.toRecipients, values.ccRecipients);
            setSendToMasterData(toRecipientsData);
            setCopyToMasterData(ccRecipientsData);
        } catch (error) {
            toast.error(translate.byKey(TranslationKeys.error));
        } finally {
            setBusy(false);
        }
    }

    return <WizardSummaryLayout onBack={onBack} submitLabel={translate.byKey(TranslationKeys.confirm)}>
            {/* page title */}
            <h1 className={`mb-3 text-portal-blue-primary-shade2 text-xl font-semibold`}>
                {translate.byKey(TranslationKeys.summary)}
            </h1>

            <div style={{overflowY: "auto", height: "380px", overflowX: "hidden"}}>
                {/* notification type */}
                <div className="flex pb-3">
                    <div className="w-1/3">
                        <h4 className="p-1 text-xs text-gray-600">
                            {translate.byKey(TranslationKeys.selectNotificationType)}:
                        </h4>
                    </div>
                    <div className="w-2/3 mt-1">
                        <div className="text-sm text-black">
                            {taskDescription}
                        </div>
                    </div>
                </div>
                <hr />

                {/* notification name */}
                <div className="flex pb-3 mt-1">
                    <div className="w-1/3">
                        <h4 className="p-1 text-xs text-gray-600">
                            {translate.byKey(TranslationKeys.setNotificationName)}:
                        </h4>
                    </div>
                    <div className="w-2/3 mt-1">
                        <div className="text-sm text-black">
                            {values.name}
                        </div>
                    </div>
                </div>
                <hr />

                {/* report type */}
                <div className="flex pb-3 mt-1">
                    <div className="w-1/3">
                        <h4 className="p-1 text-xs text-gray-600">
                            {translate.byKey(TranslationKeys.describeThisReport)}:
                        </h4>
                    </div>
                    <div className="w-2/3 mt-1">
                        <div className="text-sm text-black">
                            {values.report}
                        </div>
                    </div>
                </div>
                <hr />

                {/* send to recipients */}
                <div className="flex pb-3 mt-1">
                    <div className="w-1/3">
                        <h4 className="p-1 text-xs text-gray-600">
                            {translate.byKey(TranslationKeys.sendToRecipient)}:
                        </h4>
                    </div>
                    <div className="w-1/3 mt-1">
                        {sendToMasterData.map((data) => {
                            return (
                                <div
                                    className="text-sm text-black"
                                    key={data.id}
                                >
                                    {data.name}
                                </div>
                            );
                        })}
                    </div>
                    <div className="w-1/3 mt-1">
                        {sendToMasterData.map((data) => {
                            return (
                                <div
                                    className="text-sm text-black"
                                    key={data.id}
                                >
                                    {data.email}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <hr />

                {/* cc recipients */}
                <div className="flex pb-3 mt-1">
                    <div className="w-1/3">
                        <h4 className="p-1 text-xs text-gray-600">
                            {translate.byKey(TranslationKeys.copyToRecipient)}:
                        </h4>
                    </div>
                    <div className="w-1/3 mt-1">
                        {copyToMasterData && copyToMasterData.map((data) => {
                            return (
                                <div
                                    className="text-sm text-black"
                                    key={data.id}
                                >
                                    {data.name}
                                </div>
                            );
                        })}
                    </div>
                    <div className="w-1/3  mt-1">
                        {copyToMasterData && copyToMasterData.map((data) => {
                            return (
                                <div
                                    className="text-sm text-black"
                                    key={data.id}
                                >
                                    {data.email}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <hr />

                {/* schedule */}
                <div className="flex pb-3 mt-1">
                    <div className="w-1/3">
                        <h4 className="p-1 text-xs text-gray-600">
                            {translate.byKey(TranslationKeys.schedule)}:
                        </h4>
                    </div>

                    <div className="w-2/3 mt-1">
                        <div className="flex-column">
                            <h1 className="text-sm text-black font-bold">
                                {translate.byKey(TranslationKeys.time)}:
                            </h1>
                            <span className="text-sm text-black">
                                {formatTime(values.time)}
                            </span>

                            {/* recurrence pattern */}
                            {buildRecurrencePatternSummary(values, translate)}


                            {/* recurrence range */}
                            {buildRecurrenceRangeSummary(values, translate)}
                        </div>
                    </div>
                </div>
                <hr />

                {/* email template */}
                <div className="flex pb-3 mt-1">
                    <div className="w-1/3">
                        <h4 className="p-1 text-xs text-gray-600">
                            {translate.byKey(TranslationKeys.emailTemplate)}:
                        </h4>
                    </div>
                    <div className="w-2/3 mt-1">
                        <div className="flex-column">
                            <h1 className="text-sm text-black font-bold">
                                {translate.byKey(TranslationKeys.emailSubject)}:
                            </h1>
                            <span className="text-sm text-black">
                                {values.subject}
                            </span>

                            {values.body ?
                                (
                                    <div className="flex-column mt-3">
                                        <h1 className="text-sm text-black font-bold">
                                            {translate.byKey(TranslationKeys.emailBody)}:
                                        </h1>
                                        <div className="vertical-scroll text-sm text-black">
                                            {values.body}
                                        </div>
                                    </div>
                                )
                                :
                                <></>
                            }

                            {values.disclaimer ?
                                (
                                    <div className="flex-column mt-3">
                                        <h1 className="text-sm text-black font-bold">
                                            {translate.byKey(TranslationKeys.disclaimer)}:
                                        </h1>
                                        <div className="text-sm text-black">
                                            {values.disclaimer}
                                        </div>
                                    </div>
                                )
                                :
                                <></>
                            }
                        </div>
                    </div>
                </div>
            </div>
    </WizardSummaryLayout>
}

export default Summary;
